<template>
    <main>
        <div class="heroCont" v-if="data && data.page">
            <div class="infoBar">
                <WebsiteBreadcrumbs>
                    <span>publications</span>
                </WebsiteBreadcrumbs>
            </div>
            <h1 class="helvetica title1">{{ data.page?.title }}</h1>
            <div class="line"></div>
            <div class="body" v-if="data.page.introduction" v-html="data.page.introduction"></div>
        </div>
        <div class="filters" v-if="themeOptions && themeOptions.length > 0">
            <NSelect v-model:value="theme" placeholder="Themes" :options="themeOptions" size="large" :show-checkmark="false"></NSelect>
        </div>
        <div class="papers" v-if="data && data.papers">
            <BlockPublications :publications="data.papers.data" :meta="data.papers.meta" :page="1" @update-page="pageUpdate" />
        </div>
        <WebsiteFooter v-if="data && data.page" />
    </main>
</template>

<script lang="ts" setup>
import { NSelect } from 'naive-ui';

const { getPositionPapersPage } = usePublications();
const directusUrl = useDirectusUrl()
const route = useRoute()
const router = useRouter()

const theme = ref(route.query.theme as string | null)

const { data } = useAsyncData(`publications-position-papers-1`, () => getPositionPapersPage(1, theme.value), { watch: [theme] });

const themeOptions = computed(() => {
    if (data.value && data.value.themes) {
        return [
            { label: 'All themes', value: null },
            ...data.value.themes.map((theme: any) => {
                return {
                    label: theme.title,
                    value: theme.url
                }
            })
        ]
    } else return []
})

const pageUpdate = (newPage: number) => {
    if (newPage > 1) router.push({ path: `/publications/position-papers/p/${newPage}`, query: { theme: theme.value || undefined } })
    else router.push({ path: '/publications/position-papers', query: { theme: theme.value || undefined } })
}

watch(theme, () => {
    if (theme.value) router.replace({ query: { theme: theme.value } })
    else router.replace({ query: { theme: undefined } })
})

useSeoMeta({
    title: () => `${data.value?.page?.title} | Publications | EIC Federation`,
    ogTitle: () => `${data.value?.page?.title} | Publications | EIC Federation`,
    twitterTitle: () => `${data.value?.page?.title} | Publications | EIC Federation`,

    description: () => data.value?.page?.summary,
    ogDescription: () => data.value?.page?.summary,
    twitterDescription: () => data.value?.page?.summary,

    ogImage: () => data.value?.page?.cover?.id ? directusUrl + '/assets/' + data.value?.page.cover.id : undefined,
})
</script>

<style lang="scss" scoped>
main {
    padding-top: var(--size-padding-header);

    .heroCont {
        margin: 0 var(--size-margin-page) var(--size-gutter-double) var(--size-margin-page);
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0 var(--size-margin-page);

        .infoBar {
            margin-bottom: var(--size-gutter-default);
            color: var(--color-primary);

            .date {
                display: flex;
                gap: var(--size-gutter-default);
            }
        }

        h1 {
            grid-column: 1 / span 2;
            margin-bottom: var(--size-gutter-double);
        }

        .line {
            grid-column: 1 / span 2;
            height: var(--size-thickness);
            background-color: var(--color-primary);
        }

        .body {
            margin-top: var(--size-gutter-default);
            grid-column: 2;
            font-weight: bold;
        }
    }

    .filters {
        display: grid;
        grid-template-columns: repeat(8, minmax(0, 1fr));
        gap: var(--size-gutter-small);
        margin-bottom: var(--size-gutter-double);
        padding: 0 var(--size-margin-page);

        &:deep(.n-select) {
            .n-base-selection .n-base-selection-label {
                --n-color: white;
                --n-color-active: white;

                .n-base-selection-input,
                .n-base-selection-placeholder__inner {
                    color: var(--color-primary);
                }
            }

            .n-base-selection .n-base-selection__border,
            .n-base-selection .n-base-selection__state-border {
                --n-border: var(--size-thickness) solid var(--color-primary);
            }
        }
    }
}

@media screen and (max-width: 1200px) {
    main {
        .heroCont {
            .body,
            .downloadsCont {
                grid-column: 1 / span 2;
            }
        }

        .filters {
            grid-template-columns: repeat(4, minmax(0, 1fr));
        }
    }
}

@media screen and (max-width: 600px) {
    main {
        .filters {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }
    }
}
</style>